import React, { Component } from 'react';
import moment from 'moment';

import PurchaseOrderHeading from './PurchaseOrderHeading';
import LineItems from './LineItems/LineItems'
import PurchaseOrderTotals from './PurchaseOrderTotals';
import PurchaseOrderActions from './PurchaseOrderActions';
import Activity from './Activity'
import * as defs from './PurchaseOrder.module.scss';

import {
  approvePurchaseOrder,
  updatePurchaseOrder,
  createPurchaseOrder,
  closePurchaseOrder,
  deletePurchaseOrder
} from '../../api/resources/PurchaseOrders';

import { getCostCentres } from '../../api/resources/CostCentres';
import { getRequiresApprovalOptions } from './stateChangeUtilities.js';
import { isArray, isObject } from 'lodash';

const STORAGE_KEY = "sel_currentPurchaseOrderState";

const isEmpty = input => {
  if (typeof input === 'number') return false;
  if (input === null) return true;
  if (input === undefined) return true;
  return (Object.keys(input).length === 0);
}

const isDefaultValue = (key, value) => {
  return ({
    "cost": 0,
    "quantity": 1,
    "spendDate": new Date().toISOString().split('T')[0],
    "vatPercent": 0
  }[key] === value);
}

const propertiesThatMatter = [
  "supplier",
  "location",
  "name",
  "spendDate",
  "contractNumber",
  "vatPercent",
  "description",
  "cost",
  "quantity",
  "costCentre",
  "lineItems"
];

const hasValuesThatMatter = purchaseOrder => {
  const getValues = object => {
    if (isArray(object)) {
      return object.some(getValues);
    } else if (isObject(object)) {
      return Object.entries(object).some(([key, value]) => {
        if (!propertiesThatMatter.includes(key)) return false;
        if (isEmpty(value) || isDefaultValue(key, value)) return false;
        if (isArray(value)) return getValues(value);
        if (isObject(value)) return getValues(value);
        return true;
      });
    }
  };

  return getValues(purchaseOrder);
}

export default class PurchaseOrder extends Component {

  constructor(props) {
    super(props)
    this.state = {
      purchaseOrder: null,
      savedPurchaseOrder: null,
      costCentres: [],
      errors: {},
      isSaving: false
    }
    this.approve = this.approve.bind(this)
    this.handleStartEdit = this.handleStartEdit.bind(this)
    this.handleStartEditingLineItems = this.handleStartEditingLineItems.bind(this)
    this.handleStatusChange = this.handleStatusChange.bind(this)
    this.handleCancelEdit = this.handleCancelEdit.bind(this)
    this.handleCancelEditingLineItems = this.handleCancelEditingLineItems.bind(this)
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleUpdateLineItems = this.handleUpdateLineItems.bind(this);
    this.handleUpdateTotals = this.handleUpdateTotals.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.createOrUpdate = this.createOrUpdate.bind(this);
    this.onError = this.onError.bind(this);
    this.validationErrors = this.validationErrors.bind(this);
    this.close = this.close.bind(this);
    this.create = this.create.bind(this);
    this.update = this.update.bind(this);
  }

  setLocal(stateObj) {
    const oldStateObj = this.getLocal();
    const payload = JSON.stringify({...oldStateObj, ...stateObj });
    localStorage.setItem(STORAGE_KEY, payload);
  }

  getLocal() {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  }

  clearLocal() {
    return localStorage.removeItem(STORAGE_KEY);
  }

  _setState(stateObj, callback, setLocal = true) {
    if (setLocal) {
      stateObj.cacheTimestamp = Date.now();
      this.setLocal(stateObj);
    }
    return this.setState(stateObj, callback);
  }

  componentDidMount() {
    let { purchaseOrder, user } = this.props;

    this.getCostCentres();

    const stateObj = {
      purchaseOrder,
      userEmail: user.email
    };

    const restorablePurchaseOrder = this.getRestorableState();
    if (restorablePurchaseOrder) {
      stateObj.purchaseOrder = restorablePurchaseOrder;
    } else {
      this.clearLocal();
    }

    this._setState(stateObj, () => {
      if (!purchaseOrder.id) this.handleStartEdit();
    });

  }

  getRestorableState() {
    const { purchaseOrder, user } = this.props;
    const { cacheTimestamp, userEmail, purchaseOrder: storedPurchaseOrder } = (this.getLocal() || {});
    const timeStampAgeInHours = moment().diff(moment(cacheTimestamp), 'hours');

    if (
      hasValuesThatMatter(storedPurchaseOrder) &&
      timeStampAgeInHours < 24 &&
      userEmail === user.email &&
      !purchaseOrder.id &&
      !storedPurchaseOrder.id &&
      confirm("Restore previously discarded Purchase Order?")
    ) {
      return storedPurchaseOrder;
    } else {
      return null;
    }
  }

  async getCostCentres() {
    await getCostCentres().then(res => {
      this._setState({costCentres: res.reduce((obj, costCentre) => {
        obj[costCentre.name] = costCentre;
        return obj;
      }, {})});
    });
  }

  onError(err) {
    console.error(err);
    this._setState({isSaving: false})
    alert("Something went wrong.")
  }

  handleStartEdit() {
    const { purchaseOrder } = this.state;
    const savedPurchaseOrder = structuredClone(purchaseOrder);
    this._setState({
      purchaseOrder: { ...purchaseOrder, isEditing: true },
      savedPurchaseOrder
    });
  }

  handleCancelEdit() {
    const purchaseOrder = structuredClone(this.state.savedPurchaseOrder);
    this._setState({
      purchaseOrder: { ...purchaseOrder, isEditing: false }
    })
  }

  handleStartEditingLineItems() {
    const { purchaseOrder } = this.state;
    const { lineItems } = purchaseOrder;
    const savedLineItems = structuredClone(lineItems);
    this._setState({
      savedLineItems,
      purchaseOrder: {
        ...purchaseOrder,
      }
    });
  }

  handleCancelEditingLineItems() {
    const { purchaseOrder } = this.state;
    const lineItems = structuredClone(this.state.savedLineItems) || [];
    this._setState({
      purchaseOrder: { ...purchaseOrder, lineItems },
      savedPurchaseOrder: { ...purchaseOrder, lineItems },
    });
  }

  handleUpdate(payload) {
    const [field, value] = Object.entries(payload)[0];
    this._setState({
      purchaseOrder: {
        ...this.state.purchaseOrder,
        [field]: value,
      },
      errors: {}
    })
  }

  handleUpdateTotals({ subtotal, vat, vatPercent, total }) {
    this._setState({
      purchaseOrder: {
        ...this.state.purchaseOrder,
        subtotal,
        vat,
        vatPercent,
        total,
      },
    })
  }

  handleUpdateLineItems(lineItems, save) {
    const { lineItems: _discard, ...errors } = this.state.errors
    const stateObj = {
      purchaseOrder: {
        ...this.state.purchaseOrder,
        lineItems,
      },
      errors
    };
    if (save) stateObj.savedLineItems = lineItems;
    this._setState(stateObj)
  }

  handleStatusChange(res) {
    this._setState({
      purchaseOrder: {
        ...this.state.purchaseOrder,
        activities: res.activities,
        status: res.status
      },
    })
  }

  validationErrors() {
    const { purchaseOrder } = this.state;
    let errors = {};
    if (!purchaseOrder.supplier) errors.supplier = 'Required';
    if (!purchaseOrder.location) errors.location = 'Required';
    if (purchaseOrder.lineItems.length === 0) errors.lineItems = 'Add at least one';
    if (Object.keys(errors).length > 0) {
      this._setState({errors})
      return true;
    }
  }

  approve(comment, isEditsPreApproved) {
    const { purchaseOrder } = this.state;
    purchaseOrder.isEditsPreApproved = isEditsPreApproved;
    this._setState({isSaving: true})
    return new Promise((resolve, reject) => approvePurchaseOrder(purchaseOrder, comment).then(res => {
      this._setState({
        isSaving: false,
        purchaseOrder: {
          ...purchaseOrder,
          purchaseOrderNumber: res.purchaseOrderNumber,
          activities: res.activities,
          status: res.status,
          approvedTs: (res.approvedTs) ? moment(res.approvedTs.value).format('YYYY-MM-DD'): null,
        }
      })
      resolve();
    }).catch(err => {
      reject();
      this.onError(err);
    }))
  }
  
  isClosable() {
    const { lineItems } = this.state.purchaseOrder;
    return (lineItems.filter(li => li.received === li.quantity)).length === lineItems.length;
  }

  close() {
    if (!this.isClosable()) {
      alert("Mark all line items as received before closing a purchase order")
      return false;
    } else {
      if (confirm("Is the invoice correct? Closing purchase orders cannot be undone.")) {
        this._setState({isSaving: true})
        closePurchaseOrder(this.state.purchaseOrder.id).then(res => {
          this._setState({
            isSaving: false,
            purchaseOrder: {
              ...this.state.purchaseOrder,
              status: res.status,
              activities: res.activities
            }
          })
          alert("Purchase order closed.")
        }).catch(this.onError)
      }
    }
  }

  handleSubmit(comment, isProvisional) {
    if (this.validationErrors()) return false
    return this.createOrUpdate(comment, isProvisional);
  }

  handleDelete() {
    if (confirm("Are you sure you want to delete this purchase order?")) {
      let { purchaseOrder } = this.state;
      deletePurchaseOrder(purchaseOrder.id).then(res => {
        alert("Purchase order deleted.");
        this.props.history.push(`/purchase-orders`);
        this.props.history.go(0)
      })
    }
  }

  create(purchaseOrder, comment, isProvisional) {
    purchaseOrder.isProvisional = isProvisional;
    createPurchaseOrder(purchaseOrder, comment).then(res => {
      this.clearLocal();
      this.props.history.push(`/purchase-orders/${res.id}`);
      this.props.history.go(0);
    }).catch(this.onError);
  }

  update(purchaseOrder, comment) {
    updatePurchaseOrder(purchaseOrder, comment).then(res => {
      purchaseOrder.activities = res.activities;
      purchaseOrder.lineItems = res.lineItems;
      this._setState({
        isSaving: false,
        purchaseOrder: { ...purchaseOrder, isEditing: false }
      });
    }).catch(this.onError);
  }

  createOrUpdate(comment, isProvisional) {

    return new Promise((resolve, reject) => {

      let { purchaseOrder, savedPurchaseOrder, costCentres } = this.state;
      const { user } = this.props;

      if (JSON.stringify(purchaseOrder) === JSON.stringify(savedPurchaseOrder)) {
        return this._setState({purchaseOrder: {...purchaseOrder, isEditing: false }, isSaving: false }, reject);
      }

      this._setState({isSaving: true})

      const { confirmText, newStatus, autoComment, isEscalated } = getRequiresApprovalOptions(purchaseOrder, savedPurchaseOrder, user, costCentres);

      if (confirmText) {
        if (confirm(confirmText)) {
          if (newStatus) purchaseOrder.status = newStatus;
          if (isEscalated) purchaseOrder.isEscalated = isEscalated;
        } else {
          return this._setState({isSaving: false}, reject);
        }
      }

      if (purchaseOrder.id) {
        this.update(purchaseOrder, autoComment);
      } else {
        this.create(purchaseOrder, comment, isProvisional);
      }

      resolve();

    })

  }

  render() {

    const { purchaseOrder, isSaving, errors } = this.state
    const isEditing = purchaseOrder ? purchaseOrder.isEditing : false;
    const { user } = this.props
    const _props = { purchaseOrder, isEditing, user }

    return (
      <>
        { purchaseOrder &&
          <div className={defs.purchaseOrderContainer}>
            <PurchaseOrderHeading
              {..._props}
              onUpdate={this.handleUpdate}
              errors={errors} />
            <LineItems
              lineItems={purchaseOrder.lineItems}
              isEditable={isEditing}
              onStartEdit={this.handleStartEditingLineItems}
              onCancelEdit={this.handleCancelEditingLineItems}
              onUpdate={this.handleUpdateLineItems}
              error={errors.lineItems} />
            <PurchaseOrderTotals
              purchaseOrder={purchaseOrder}
              isEditing={isEditing}
              onUpdate={this.handleUpdateTotals} />
            <Activity activities={purchaseOrder.activities} />
            <div className={defs.actionsContainer} style={{marginTop: '32px'}}>
              <PurchaseOrderActions
                {..._props}
                isSaving={isSaving}
                isEditingLineItems={purchaseOrder.lineItems.some(li => li.isEditing)}
                onApprove={this.approve}
                onClose={this.close}
                onStatusChange={this.handleStatusChange}
                onStartEdit={this.handleStartEdit}
                onCancelEdit={this.handleCancelEdit}
                onUpdate={this.handleUpdateLineItems}
                onDelete={this.handleDelete}
                onSubmit={this.handleSubmit}
                validationErrors={this.validationErrors}
              />
            </div>
          </div>
        }
      </>
    );
  }
}